import { coreApi } from '$api'
import { ILeaveType, LeaveType } from '$models'
import { singletonQueryClient } from '$utils'
import { LeaveTypeType } from '$constants'
import { orderBy } from 'lodash'

enum QueryKeys {
  LeaveTypes = 'leave-types',
  LeaveType = 'leave-type',
}

/**
 * Get Leave Types list
 * @returns Promise<ILeaveType[]>
 */
export const getAll = async (staffMemberId?: string): Promise<ILeaveType[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.LeaveTypes, staffMemberId],
    queryFn: async () => {
      // Leave types are sorted first by type (Managed first) then by name
      const leaveTypes = await coreApi.getLeaveTypes(staffMemberId)
      return orderBy(
        leaveTypes,
        [(item) => (item.type === LeaveTypeType.Managed ? 0 : 1), 'name'],
        ['asc', 'asc']
      )
    },
  })
}

/**
 * Get Leave Type by Id
 * @param leaveTypeId - leave type id to get
 * @returns Promise<ILeaveType>
 */
export const getById = async (leaveTypeId: string): Promise<ILeaveType> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.LeaveTypes, leaveTypeId],
    queryFn: async () => {
      return await coreApi.getLeaveTypeById(leaveTypeId)
    },
  })
}

/**
 * Update Leave Type
 * @param leaveType - leave type to update
 * @returns Promise<ILeaveType>
 */
export const update = async (leaveType: ILeaveType): Promise<ILeaveType> => {
  const result = await coreApi.updateLeaveType(
    leaveType.id!,
    leaveType as LeaveType
  )
  return result
}

/**
 * Enable Leave Type
 * @param leaveType - leave type to enable
 * @returns Promise<ILeaveType>
 */
export const enable = async (leaveType: ILeaveType): Promise<ILeaveType> => {
  const result = await coreApi.activateLeaveType(
    leaveType.id!,
    leaveType as LeaveType
  )
  return result
}

/**
 * Add Leave Type
 * @param leaveType - leave type to update
 * @returns Promise<ILeaveType>
 */
export const add = async (leaveType: ILeaveType): Promise<ILeaveType> => {
  const newLeaveType = await coreApi.createLeaveType(leaveType as LeaveType)
  return newLeaveType
}

/**
 * Delete Leave Type
 * @param leaveTypeId - leave type id to delete
 * @returns Promise<void>
 */
export const remove = async (leaveTypeId: string): Promise<void> => {
  return await coreApi.deactivateLeaveType(leaveTypeId)
}
